import { signal } from "@preact/signals-core";
import { ApiRequestError } from "../lib/api";
import { bindErrorText, bindValue, html, list } from "../lib/bind";
import { bindSelect, setSelectOptions } from "../lib/formBind";
import { form } from "../lib/form";
import { currentScope } from "../lib/scope";
import { labelRow } from "../lib/table";
import { toast } from "../lib/toast";
import { breadcrumb } from "../stores/breadcrumb";
import {
  type BankAccount,
  type ImportProfile,
  archiveBankAccount,
  createBankAccount,
  getBankAccount,
  listBankAccounts,
  listImportProfiles,
  updateBankAccount,
} from "../services/bank";

function emptyAccountInput() {
  return {
    name: "",
    bank_name: "",
    currency: "GBP",
    sort_code: "",
    account_number: "",
    import_profile_id: "",
  };
}

export function bankAccountsListView(): Node {
  const accounts = signal<BankAccount[]>([]);
  const loadError = signal("");

  function reload(): void {
    listBankAccounts()
      .then((result) => {
        accounts.value = result;
      })
      .catch((err: unknown) => {
        loadError.value = err instanceof ApiRequestError ? err.body.message : "Failed to load";
      });
  }
  reload();

  async function doArchive(a: BankAccount): Promise<void> {
    if (!window.confirm(`Archive bank account "${a.name}"?`)) return;
    try {
      await archiveBankAccount(a.id);
      reload();
      toast.success("Bank account archived.");
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Failed to archive account");
    }
  }

  const fragment = html`<div class="view">
    <p class="form-error"></p>

    <div class="card">
      <div class="card-label-row">
        <a href="/bank-accounts/new" data-link class="btn btn-primary">Add account</a>
      </div>
      <div class="data-table accounts-table">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Bank</th>
              <th>Sort code</th>
              <th>Account number</th>
              <th>Currency</th>
              <th>Profile</th>
              <th></th>
            </tr>
          </thead>
          <tbody class="accounts-body"></tbody>
        </table>
      </div>
    </div>
  </div>`;

  const errorEl = fragment.querySelector<HTMLParagraphElement>(".form-error");
  const accountsTable = fragment.querySelector<HTMLTableElement>(".accounts-table table");
  const accountsBody = fragment.querySelector<HTMLTableSectionElement>(".accounts-body");
  if (!errorEl || !accountsTable || !accountsBody) {
    throw new Error("bank accounts list markup missing expected elements");
  }
  bindErrorText(errorEl, loadError);

  list(
    accountsBody,
    accounts,
    (a) => a.id,
    (a) => {
      const row = html`<tr>
        <td><a href="/bank-accounts/${a.id}" data-link>${a.name}</a></td>
        <td>${a.bank_name ?? "—"}</td>
        <td>${a.sort_code ?? "—"}</td>
        <td>${a.account_number ?? "—"}</td>
        <td>${a.currency}</td>
        <td>${a.import_profile_name ?? "—"}</td>
        <td><button class="btn btn-danger acct-archive-btn" type="button">Archive</button></td>
      </tr>`.firstElementChild;
      if (!row) throw new Error("account row template produced no element");
      const archiveBtn = row.querySelector<HTMLButtonElement>(".acct-archive-btn");
      archiveBtn?.addEventListener("click", () => {
        void doArchive(a);
      });
      return labelRow(row, accountsTable);
    },
  );

  return fragment;
}

export function bankAccountDetailView(params: Record<string, string>): Node {
  const paramId = params.id ?? "";
  const isNew = paramId === "new";
  const accountId = signal(isNew ? "" : paramId);
  const savedAt = signal("");
  const errorMessage = signal("");
  const profiles = signal<ImportProfile[]>([]);

  breadcrumb.value = { label: "Bank Accounts", path: "/bank-accounts" };
  currentScope().cleanup(() => {
    breadcrumb.value = null;
  });

  listImportProfiles()
    .then((result) => {
      profiles.value = result;
    })
    .catch(() => undefined);

  const accountForm = form(emptyAccountInput());

  if (!isNew) {
    getBankAccount(paramId)
      .then((account) => {
        accountForm.reset({
          name: account.name,
          bank_name: account.bank_name ?? "",
          currency: account.currency,
          sort_code: account.sort_code ?? "",
          account_number: account.account_number ?? "",
          import_profile_id: account.import_profile_id ?? "",
        });
        savedAt.value = account.updated_at;
      })
      .catch((err: unknown) => {
        errorMessage.value = err instanceof ApiRequestError ? err.body.message : "Failed to load";
      });
  }

  async function submit(event: Event): Promise<void> {
    event.preventDefault();
    errorMessage.value = "";
    if (!accountForm.fields.name.value.trim()) {
      errorMessage.value = "Enter an account name.";
      return;
    }
    const input = {
      name: accountForm.fields.name.value,
      bank_name: accountForm.fields.bank_name.value || null,
      currency: accountForm.fields.currency.value || "GBP",
      sort_code: accountForm.fields.sort_code.value || null,
      account_number: accountForm.fields.account_number.value || null,
      import_profile_id: accountForm.fields.import_profile_id.value || null,
    };
    try {
      if (isNew) {
        const created = await createBankAccount(input);
        toast.queueSuccess("Bank account created.");
        location.assign(`/bank-accounts/${created.id}`);
        return;
      }
      const updated = await updateBankAccount(accountId.value, {
        ...input,
        updated_at: savedAt.value,
      });
      savedAt.value = updated.updated_at;
      toast.success("Bank account saved.");
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 412) {
        errorMessage.value = "This record changed elsewhere — reload and retry.";
      } else {
        errorMessage.value = err instanceof ApiRequestError ? err.body.message : "Save failed";
      }
      toast.error(errorMessage.value);
    }
  }

  async function doArchive(): Promise<void> {
    if (!window.confirm("Archive this bank account?")) return;
    try {
      await archiveBankAccount(accountId.value);
      toast.queueSuccess("Bank account archived.");
      location.assign("/bank-accounts");
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Failed to archive account");
    }
  }

  const fragment = html`<div class="view">
    <div class="page-header"><h1>${isNew ? "New bank account" : "Edit bank account"}</h1></div>
    <p class="form-error"></p>

    <form class="form account-form" onsubmit="${submit}">
      <div class="form-row">
        <div class="form-field">
          <label for="acct-name">Name</label>
          <input id="acct-name" required />
        </div>
        <div class="form-field">
          <label for="acct-bank-name">Bank</label>
          <input id="acct-bank-name" placeholder="optional" />
        </div>
        <div class="form-field">
          <label for="acct-currency">Currency</label>
          <input id="acct-currency" />
        </div>
      </div>
      <div class="form-row">
        <div class="form-field">
          <label for="acct-sort-code">Sort code</label>
          <input id="acct-sort-code" placeholder="optional, display only" />
        </div>
        <div class="form-field">
          <label for="acct-account-number">Account number</label>
          <input id="acct-account-number" placeholder="optional, display only" />
        </div>
        <div class="form-field acct-profile-field">
          <label for="acct-profile">Import profile</label>
          <select id="acct-profile"><option value="">(none)</option></select>
        </div>
      </div>
      <div class="form-actions">
        <button class="btn btn-primary" type="submit">Save</button>
        <button class="btn btn-danger archive-btn" type="button">Archive</button>
      </div>
    </form>
  </div>`;

  const errorEl = fragment.querySelector<HTMLParagraphElement>(".form-error");
  const nameInput = fragment.querySelector<HTMLInputElement>("#acct-name");
  const bankNameInput = fragment.querySelector<HTMLInputElement>("#acct-bank-name");
  const currencyInput = fragment.querySelector<HTMLInputElement>("#acct-currency");
  const sortCodeInput = fragment.querySelector<HTMLInputElement>("#acct-sort-code");
  const accountNumberInput = fragment.querySelector<HTMLInputElement>("#acct-account-number");
  const profileSelect = fragment.querySelector<HTMLSelectElement>("#acct-profile");
  const archiveBtn = fragment.querySelector<HTMLButtonElement>(".archive-btn");

  if (
    !errorEl ||
    !nameInput ||
    !bankNameInput ||
    !currencyInput ||
    !sortCodeInput ||
    !accountNumberInput ||
    !profileSelect ||
    !archiveBtn
  ) {
    throw new Error("bank account detail markup missing expected elements");
  }
  if (isNew) archiveBtn.style.display = "none";

  bindErrorText(errorEl, errorMessage);
  bindValue(nameInput, accountForm.fields.name);
  bindValue(bankNameInput, accountForm.fields.bank_name);
  bindValue(currencyInput, accountForm.fields.currency);
  bindValue(sortCodeInput, accountForm.fields.sort_code);
  bindValue(accountNumberInput, accountForm.fields.account_number);
  bindSelect(profileSelect, accountForm.fields.import_profile_id);
  archiveBtn.addEventListener("click", () => {
    void doArchive();
  });

  // Populating options and applying the loaded account's current selection are two
  // independent async events (listImportProfiles() vs. getBankAccount()) racing each
  // other — bindSelect's own effect only re-fires on the field's value changing, so if
  // the field is set before the <option>s exist, the browser silently drops the
  // selection and nothing re-applies it once the options do arrive. Reading both
  // signals in one effect and re-asserting the value after every repopulation closes
  // that race regardless of which promise resolves first.
  currentScope().effect(() => {
    setSelectOptions(
      profileSelect,
      profiles.value.map((p) => ({ value: p.id, label: p.name })),
      { placeholder: "(none)" },
    );
    profileSelect.value = accountForm.fields.import_profile_id.value;
  });

  return fragment;
}
